/* ========================================================================
   Product Upsell Add-on Block — JavaScript
   Custom element that intercepts the main ATC button and adds checked
   upsell products to the cart alongside the main product.
   ======================================================================== */

!function () {
  'use strict';

  class UpsellAddon extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.mainVariantInput = null;
      this.mainQuantityInput = null;
      this.mainForm = null;
      this.totalEl = null;
      this.totalSaleEl = null;
      this.totalCompareEl = null;
      this.intercepted = false;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      // Collect all upsell item elements
      this.items = Array.from(this.querySelectorAll('[data-upsell-item]'));

      // Total display
      this.totalEl = this.querySelector('[data-upsell-total]');
      this.totalSaleEl = this.querySelector('[data-upsell-total-sale]');
      this.totalCompareEl = this.querySelector('[data-upsell-total-compare]');

      // Find the main product form
      const section = this.closest('[data-section-type="product"]') || this.closest('product-component');
      if (section) {
        this.mainForm = section.querySelector('form[data-product-form]');
        this.mainVariantInput = section.querySelector('input[name="id"]');
        this.mainQuantityInput = section.querySelector('input[name="quantity"]');
      }

      // Setup checkbox listeners
      this.items.forEach(item => {
        const checkbox = item.querySelector('[data-upsell-checkbox]');
        if (checkbox) {
          checkbox.addEventListener('change', () => {
            item.classList.toggle('is-unchecked', !checkbox.checked);
            this.updateTotal();
          });
          // Initialize state
          if (!checkbox.checked) {
            item.classList.add('is-unchecked');
          }
        }

        // Setup variant pill listeners
        const pills = item.querySelectorAll('[data-upsell-variant-pill]');
        pills.forEach(pill => {
          pill.addEventListener('click', (e) => {
            e.preventDefault();
            if (pill.classList.contains('is-soldout')) return;
            this.selectVariant(item, pill);
          });
        });
      });

      // Listen for main product variant changes
      this.setupMainVariantListener();

      // Intercept the main ATC form
      this.interceptForm();

      // Calculate initial total
      this.updateTotal();
    }

    /* ---- Variant Selection ---- */
    selectVariant(item, activePill) {
      const pills = item.querySelectorAll('[data-upsell-variant-pill]');
      pills.forEach(p => p.classList.remove('is-active'));
      activePill.classList.add('is-active');

      // Update stored variant data
      const variantId = activePill.dataset.variantId;
      const price = parseInt(activePill.dataset.variantPrice, 10);
      const comparePrice = parseInt(activePill.dataset.variantCompare || '0', 10);

      item.dataset.selectedVariantId = variantId;
      item.dataset.selectedPrice = price;
      item.dataset.selectedCompare = comparePrice;

      // Update displayed price
      const priceContainer = item.querySelector('[data-upsell-price-container]');
      if (priceContainer) {
        priceContainer.innerHTML = this.renderPrice(price, comparePrice);
      }

      // Update variant info text
      const variantInfo = item.querySelector('[data-upsell-variant-info]');
      if (variantInfo) {
        variantInfo.textContent = activePill.dataset.variantTitle || '';
      }

      this.updateTotal();
    }

    renderPrice(price, comparePrice) {
      const formatted = this.formatMoney(price);
      if (comparePrice && comparePrice > price) {
        const formattedCompare = this.formatMoney(comparePrice);
        return `<span class="upsell-addon__price--sale">${formatted}</span>
                <span class="upsell-addon__price--compare">${formattedCompare}</span>`;
      }
      return `<span class="upsell-addon__price--regular">${formatted}</span>`;
    }

    /* ---- Total Calculation ---- */
    updateTotal() {
      if (!this.totalSaleEl) return;

      let totalSale = 0;
      let totalCompare = 0;

      // Add main product price
      if (this.mainVariantInput) {
        const mainSection = this.closest('[data-section-type="product"]') || this.closest('product-component');
        const productJson = mainSection ? mainSection.querySelector('[data-product-json]') : null;
        if (productJson) {
          try {
            const product = JSON.parse(productJson.textContent);
            const variantId = parseInt(this.mainVariantInput.value, 10);
            const variant = product.variants.find(v => v.id === variantId);
            if (variant) {
              const qty = this.mainQuantityInput ? parseInt(this.mainQuantityInput.value, 10) || 1 : 1;
              totalSale += variant.price * qty;
              totalCompare += (variant.compare_at_price || variant.price) * qty;
            }
          } catch (e) { /* ignore */ }
        }
      }

      // Add checked upsell items
      this.items.forEach(item => {
        const checkbox = item.querySelector('[data-upsell-checkbox]');
        if (checkbox && checkbox.checked) {
          const price = parseInt(item.dataset.selectedPrice || '0', 10);
          const compare = parseInt(item.dataset.selectedCompare || '0', 10);
          totalSale += price;
          totalCompare += compare > 0 ? compare : price;
        }
      });

      this.totalSaleEl.innerHTML = this.formatMoney(totalSale);
      if (this.totalCompareEl) {
        if (totalCompare > totalSale) {
          this.totalCompareEl.innerHTML = this.formatMoney(totalCompare);
          this.totalCompareEl.style.display = '';
        } else {
          this.totalCompareEl.style.display = 'none';
        }
      }
    }

    /* ---- Main Variant Listener ---- */
    setupMainVariantListener() {
      if (!this.mainVariantInput) return;

      // MutationObserver on value attribute
      const observer = new MutationObserver(() => this.updateTotal());
      observer.observe(this.mainVariantInput, { attributes: true, attributeFilter: ['value'] });

      // Also listen for change event
      this.mainVariantInput.addEventListener('change', () => this.updateTotal());

      // Listen for quantity changes
      if (this.mainQuantityInput) {
        this.mainQuantityInput.addEventListener('change', () => this.updateTotal());
        // Also observe input event for +/- buttons
        this.mainQuantityInput.addEventListener('input', () => this.updateTotal());
      }
    }

    /* ---- Form Interception ---- */
    interceptForm() {
      if (!this.mainForm || this.intercepted) return;
      this.intercepted = true;

      const originalSubmit = this.mainForm.addEventListener.bind(this.mainForm);

      // Intercept the submit event
      this.mainForm.addEventListener('submit', (e) => {
        const checkedItems = this.getCheckedItems();
        if (checkedItems.length === 0) return; // Let normal submit happen

        e.preventDefault();
        e.stopPropagation();

        this.addAllToCart(checkedItems);
      }, true); // Use capture phase to intercept before the theme handler

      // Also intercept the ATC button click
      const atcButton = this.mainForm.querySelector('[data-add-to-cart]');
      if (atcButton) {
        atcButton.addEventListener('click', (e) => {
          const checkedItems = this.getCheckedItems();
          if (checkedItems.length === 0) return;

          e.preventDefault();
          e.stopPropagation();

          // Show loading state on button
          atcButton.classList.add('is-loading');
          atcButton.disabled = true;

          this.addAllToCart(checkedItems).finally(() => {
            atcButton.classList.remove('is-loading');
            atcButton.disabled = false;
          });
        }, true);
      }
    }

    getCheckedItems() {
      const items = [];
      this.items.forEach(item => {
        const checkbox = item.querySelector('[data-upsell-checkbox]');
        if (checkbox && checkbox.checked) {
          const variantId = item.dataset.selectedVariantId;
          if (variantId) {
            items.push({
              id: parseInt(variantId, 10),
              quantity: 1
            });
          }
        }
      });
      return items;
    }

    async addAllToCart(upsellItems) {
      // Build items array: main product + upsells
      const allItems = [];

      // Main product
      if (this.mainVariantInput) {
        const mainId = parseInt(this.mainVariantInput.value, 10);
        const mainQty = this.mainQuantityInput ? parseInt(this.mainQuantityInput.value, 10) || 1 : 1;

        // Also collect any hidden custom quantity / discount inputs
        const customQtyInput = document.querySelector('input[data-custom-quantity-input]');
        const customDiscountInput = document.querySelector('input[data-custom-discount-input]');

        allItems.push({
          id: mainId,
          quantity: customQtyInput ? parseInt(customQtyInput.value, 10) || mainQty : mainQty,
          properties: {}
        });
      }

      // Upsell items
      upsellItems.forEach(item => {
        allItems.push({
          id: item.id,
          quantity: item.quantity
        });
      });

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: allItems })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        // Success — trigger theme events to open cart drawer
        document.dispatchEvent(new CustomEvent('theme:cart:add', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true }));

        // Also try to open cart drawer directly
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.openCartDrawer === 'function') {
          cartDrawer.openCartDrawer();
        }

        // Show "Added" feedback on ATC button
        const atcButton = this.mainForm ? this.mainForm.querySelector('[data-add-to-cart]') : null;
        if (atcButton) {
          const btnText = atcButton.querySelector('[data-add-to-cart-text]');
          const addedSpan = atcButton.querySelector('.btn__added');
          if (btnText) {
            const originalText = btnText.textContent;
            btnText.style.display = 'none';
            if (addedSpan) addedSpan.style.display = 'inline';
            atcButton.classList.add('btn--added');

            setTimeout(() => {
              btnText.style.display = '';
              if (addedSpan) addedSpan.style.display = '';
              atcButton.classList.remove('btn--added');
            }, 2000);
          }
        }

        // Update cart count
        try {
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          const countEls = document.querySelectorAll('[data-cart-count]');
          countEls.forEach(el => {
            el.textContent = cartData.item_count;
            el.setAttribute('data-cart-count', cartData.item_count);
          });
        } catch (e) { /* ignore */ }

      } catch (error) {
        console.error('Upsell addon: Error adding to cart:', error);

        // Show error on the form
        const errorsContainer = this.mainForm ? this.mainForm.querySelector('[data-cart-errors-container]') : null;
        if (errorsContainer) {
          errorsContainer.innerHTML = `<p class="product__form__error">${error.message}</p>`;
          setTimeout(() => { errorsContainer.innerHTML = ''; }, 5000);
        }
      }
    }

    /* ---- Money Formatting ---- */
    formatMoney(cents) {
      if (window.theme && window.theme.formatMoney) {
        return window.theme.formatMoney(cents, window.theme.moneyFormat || window.theme.money_format);
      }
      // Fallback for Norwegian Krone
      const value = (cents / 100).toFixed(2);
      return value.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  }

  if (!customElements.get('upsell-addon')) {
    customElements.define('upsell-addon', UpsellAddon);
  }
}();
